/**
 * Store profile — the fields the hero, stats strip and about block render
 * (spec 28 R1).
 *
 * It lives on the Storefront screen rather than in a settings section of its
 * own because this is where its effect is visible: the preview beside it
 * redraws as these change. Every field is optional, and clearing one is an
 * ordinary edit — a store that no longer wants to publish its team size should
 * be able to remove it, so the form sends every field every time.
 */
import { useEffect, useState } from "react";
import { Button, Card, Input, Textarea, Alert, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { SectionImageField } from "./section-image-field.tsx";
import { LabelledField } from "./labelled-field.tsx";

export interface StoreProfileValues {
  coverImageUrl: string | null;
  tagline: string | null;
  about: string | null;
  establishedYear: number | null;
  teamSize: string | null;
  monthlyCapacity: string | null;
}

const blank: StoreProfileValues = {
  coverImageUrl: null,
  tagline: null,
  about: null,
  establishedYear: null,
  teamSize: null,
  monthlyCapacity: null,
};

/** Empty input means "not set", never an empty string on a public page. */
const orNull = (v: string) => (v.trim() === "" ? null : v.trim());

export function StoreProfileCard({
  initial,
  canEdit,
  onSaved,
  headerless,
}: {
  initial: StoreProfileValues | undefined;
  canEdit: boolean;
  onSaved: () => void;
  /** Drop the card chrome + title — used when hosted inside an accordion. */
  headerless?: boolean;
}) {
  const [form, setForm] = useState<StoreProfileValues>(initial ?? blank);
  const [error, setError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (initial && !seeded) {
      setForm(initial);
      setSeeded(true);
    }
  }, [initial, seeded]);

  const save = trpc.storeHome.updateProfile.useMutation({
    onSuccess: () => {
      setError(null);
      toast.success("Store profile saved");
      onSaved();
    },
    onError: (e) => setError(e.message),
  });

  const dirty = JSON.stringify(form) !== JSON.stringify(initial ?? blank);
  const set = <K extends keyof StoreProfileValues>(k: K, v: StoreProfileValues[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const saveButton = canEdit ? (
    <Button
      intent="secondary"
      size="md"
      disabled={!dirty || save.isPending}
      onClick={() => save.mutate(form)}
    >
      {save.isPending ? "Saving…" : "Save profile"}
    </Button>
  ) : null;

  const fields = (
    <>
      {error ? (
        <Alert intent="bad" title="Couldn't save the profile" className="mb-3">
          {error}
        </Alert>
      ) : null}

      <div className="flex flex-col gap-4">
        <SectionImageField
          label="Cover image"
          value={form.coverImageUrl ?? undefined}
          hint="Used by the hero when a slide has no image of its own, and as the social preview."
          onChange={(url) => set("coverImageUrl", url ?? null)}
        />
        <LabelledField label="Tagline" hint={<span className="text-xs text-muted">{(form.tagline ?? "").length}/120</span>}>
          {(id) => (
          <Input id={id}
            value={form.tagline ?? ""}
            maxLength={120}
            placeholder="Knitwear from Tiruppur since 1998"
            onChange={(e) => set("tagline", orNull(e.target.value))}
          />
          )}
        </LabelledField>
        <LabelledField label="About" hint={<span className="text-xs text-muted">{(form.about ?? "").length}/1200</span>}>
          {(id) => (
          <Textarea id={id}
            value={form.about ?? ""}
            maxLength={1200}
            rows={4}
            placeholder="What you make, who you make it for, and what you're known for."
            onChange={(e) => set("about", orNull(e.target.value))}
          />
          )}
        </LabelledField>
        <div className="grid gap-3 sm:grid-cols-3">
          <LabelledField label="Established">
          {(id) => (
            <Input id={id}
              value={form.establishedYear === null ? "" : String(form.establishedYear)}
              inputMode="numeric"
              placeholder="1998"
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                set("establishedYear", digits === "" ? null : Number(digits));
              }}
            />
          )}
        </LabelledField>
          <LabelledField label="Team size">
          {(id) => (
            <Input id={id}
              value={form.teamSize ?? ""}
              maxLength={32}
              placeholder="50–100"
              onChange={(e) => set("teamSize", orNull(e.target.value))}
            />
          )}
        </LabelledField>
          <LabelledField label="Monthly capacity">
          {(id) => (
            <Input id={id}
              value={form.monthlyCapacity ?? ""}
              maxLength={32}
              placeholder="40,000 pcs"
              onChange={(e) => set("monthlyCapacity", orNull(e.target.value))}
            />
          )}
        </LabelledField>
        </div>
        <p className="text-xs text-muted">
          Ships-from city comes from your company address, and isn't edited here.
        </p>
      </div>
    </>
  );

  // Inside the accordion: no card, no title — the trigger is the heading.
  if (headerless) {
    return (
      <div className="flex flex-col gap-3">
        {fields}
        {saveButton ? <div className="flex justify-end">{saveButton}</div> : null}
      </div>
    );
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">Store profile</h2>
          <p className="text-xs text-muted">
            The hero, stats strip and about block read these. Anything you leave blank is left out
            rather than shown empty.
          </p>
        </div>
        {saveButton}
      </div>
      {fields}
    </Card>
  );
}
